import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Procurement } from '@/types/database.types';

interface UsePaymentConfirmationProcurementsProps {
  page?: number;
  pageSize?: number;
}

/**
 * Optimized hook for payment confirmation page
 * 
 * With the unified "Approve & Pay" workflow, standard procurements now have
 * expenses created at approval time. This queue is now ONLY for:
 * - Advance payment procurements that need remaining payment confirmation
 * 
 * Regular upfront and term payments no longer appear here since they are
 * fully paid at the time of finance approval.
 */
export const usePaymentConfirmationProcurements = ({ 
  page = 1, 
  pageSize = 10 
}: UsePaymentConfirmationProcurementsProps = {}) => {
  return useQuery({
    queryKey: ['payment-confirmation-procurements', page, pageSize],
    queryFn: async () => {
      console.log(`Fetching advance payment procurements needing remaining payment - Page ${page}, Size ${pageSize}...`);
      
      // Only fetch procurements that:
      // 1. Are finance-approved (approved_by is not null)
      // 2. Have advance payment structure (has_advance_payment = true)
      // 3. Advance has been confirmed but remaining payment has NOT been confirmed
      const { data: procurements, error, count } = await supabase
        .from('procurements')
        .select(`
          id,
          product_name,
          total_quantity,
          total_amount,
          source_type,
          source_id,
          status,
          created_at,
          requestor_id,
          finance:procurement_finance!inner(
            payment_term_type,
            payment_term_days,
            payment_due_date,
            approved_by,
            finance_approved_at,
            payment_confirmed,
            payment_confirmed_by,
            payment_confirmed_at,
            has_advance_payment,
            advance_payment_amount,
            remaining_payment_amount,
            advance_payment_confirmed,
            advance_payment_confirmed_by,
            advance_payment_confirmed_at,
            remaining_payment_confirmed,
            remaining_payment_confirmed_by,
            remaining_payment_confirmed_at
          ),
          quality:procurement_quality(
            damage_kg,
            compensation_kg,
            grading_type,
            preliminary_damage_kg,
            preliminary_compensation_kg,
            preliminary_damage_percentage,
            preliminary_graded_at,
            preliminary_notes,
            final_grading_expected_date,
            final_graded_at,
            is_final_grading_complete
          ),
          items:procurement_items(
            id,
            product_name,
            quantity,
            price_per_unit,
            quality_grade,
            item_quality:procurement_item_quality(
              quality_grade,
              damage_kg,
              compensation_kg,
              preliminary_damage_kg,
              preliminary_compensation_kg
            )
          ),
          requestor:user_profiles!fk_procurements_requestor(
            id,
            first_name,
            last_name,
            role
          )
        `, { count: 'exact' })
        .not('finance.approved_by', 'is', null) // Only finance-approved procurements
        .eq('finance.has_advance_payment', true) // Only advance payment procurements
        .eq('finance.advance_payment_confirmed', true) // Advance already paid
        .eq('finance.remaining_payment_confirmed', false) // Remaining NOT yet paid
        .order('created_at', { ascending: false })
        .range((page - 1) * pageSize, page * pageSize - 1);

      if (error) {
        console.error('Error fetching advance payment procurements:', error);
        throw error;
      }

      if (!procurements) {
        return { data: [], count: 0, totalPages: 0, currentPage: page, pageSize };
      }

      console.log(`Fetched ${procurements.length} advance payment procurements needing remaining payment`);

      // Fetch source names in parallel for all unique source combinations
      const sourceQueries = new Map<string, Set<string>>();
      procurements.forEach(p => {
        if (p.source_type && p.source_id) {
          if (!sourceQueries.has(p.source_type)) {
            sourceQueries.set(p.source_type, new Set());
          }
          sourceQueries.get(p.source_type)!.add(p.source_id);
        }
      });

      // Fetch all source names in parallel
      const sourceNamePromises = Array.from(sourceQueries.entries()).map(async ([sourceType, sourceIds]) => {
        const ids = Array.from(sourceIds);
        
        switch (sourceType) {
          case 'supplier':
            const { data: suppliers } = await supabase
              .from('suppliers')
              .select('id, name')
              .in('id', ids);
            return { type: 'supplier', data: suppliers || [] };
            
          case 'agent':
            const { data: agents } = await supabase
              .from('agents')
              .select('id, name')
              .in('id', ids);
            return { type: 'agent', data: agents || [] };
            
          case 'farm':
            const { data: farms } = await supabase
              .from('farms')
              .select('id, farmer_name as name')
              .in('id', ids);
            return { type: 'farm', data: farms || [] };
            
          default:
            return { type: sourceType, data: [] };
        }
      });

      const sourceResults = await Promise.all(sourceNamePromises);
      
      // Create source name lookup map
      const sourceNameMap = new Map<string, string>();
      sourceResults.forEach(result => {
        result.data.forEach((item: any) => {
          sourceNameMap.set(`${result.type}-${item.id}`, item.name);
        });
      });

      // Enrich procurements with source names
      const enrichedProcurements = procurements.map(procurement => ({
        ...procurement,
        source_name: procurement.source_type && procurement.source_id
          ? sourceNameMap.get(`${procurement.source_type}-${procurement.source_id}`) || 'Unknown'
          : 'N/A'
      }));

      console.log('Successfully enriched procurements with source names');
      return {
        data: enrichedProcurements as any[],
        count: count || 0,
        totalPages: Math.ceil((count || 0) / pageSize),
        currentPage: page,
        pageSize
      };
    },
    staleTime: 1000 * 60 * 2, // 2 minutes cache
    gcTime: 1000 * 60 * 5, // 5 minutes garbage collection
  });
};